import { useEffect, useMemo, useRef, useState } from 'react';
import type { StructuralPieceId } from '@obraplan/core/domain/shared/ids';
import {
  checkRetainingColumnFields,
  checkRetainingWallFields,
} from '@obraplan/core/domain/takeoff/retaining';
import type { Point } from '@obraplan/core/domain/takeoff/polygon';
import {
  encaixarNoMuro,
  proximoNomeDeMuro,
  proximoNomeDePilarDeContencao,
} from './contencao-da-prancha';
import { distance, metresToPixel, pixelToMetres, resolvePoint, type Pixel } from './plan-tracer';
import type { TypologyDraft, WorkspaceAction } from './typology-draft';

/**
 * O que as ferramentas "Muro" e "Pilar de contenção" fazem na prancha (Fase 27, etapa 4 —
 * D9, F6, RN-372).
 *
 * - **Muro**: dois cliques, de ponta a ponta do eixo. O painel diz o nome, a altura e a
 *   espessura, e "Adicionar muro" cadastra;
 * - **Pilar de contenção**: um clique no centro. O clique que cai sobre um muro vale o eixo
 *   dele, e o pilar copia o ângulo do muro. O painel diz o nome, a seção e a altura — que,
 *   sem ser digitada, é a do muro em que o pilar encaixou.
 *
 * Com a prancha parada, clicar numa peça a seleciona; arrastar uma ponta do muro move só ela,
 * e arrastar o pilar o move, reencaixando ao soltar sobre um muro. Mover o muro **não** leva
 * os pilares: o que deixa de estar encaixado fica solto, e a prancha o avisa. O estado mora
 * aqui, e não dentro do traçado, pelo mesmo motivo do hook do baldrame.
 */

export type FerramentaDaContencao = 'MURO' | 'PILAR_CONTENCAO';

/** O que se pode selecionar na prancha, entre as peças desta fatia. */
export interface PecaDaContencao {
  readonly tipo: 'MURO' | 'PILAR_CONTENCAO';
  readonly id: StructuralPieceId;
}

/** A peça na mão, arrastada: o pixel por onde ela passa até o botão subir. */
export type ArrastoDaContencao =
  | {
      readonly tipo: 'PILAR_CONTENCAO';
      readonly id: StructuralPieceId;
      readonly origem: Pixel;
      readonly atual: Pixel;
      /** Passou da folga do clique? Antes disso é clique, e clicar seleciona. */
      readonly moveu: boolean;
    }
  | {
      readonly tipo: 'PONTA';
      readonly id: StructuralPieceId;
      readonly ponta: 'de' | 'ate';
      readonly origem: Pixel;
      readonly atual: Pixel;
      readonly moveu: boolean;
    };

/** A folga do clique, em pixels **de tela**, como a do encaixe do contorno. */
const FOLGA_DO_ARRASTO = 4;
const FOLGA_PONTO = 10;
const FOLGA_ORTOGONAL = 8;

/** Quanto tempo depois de soltar um arrasto o clique que veio com ele ainda é dele. */
const JANELA_DO_CLIQUE_MS = 100;

/** Altura do pilar quando nada a sugere — nem o painel, nem um muro. Metros. */
const ALTURA_PADRAO_DO_PILAR = 3;

/** O alvo de um arrasto, lido do elemento em que o botão desceu. */
function lerAlvo(
  alvo: Element,
):
  | { readonly tipo: 'PILAR_CONTENCAO'; readonly id: StructuralPieceId }
  | { readonly tipo: 'PONTA'; readonly id: StructuralPieceId; readonly ponta: 'de' | 'ate' }
  | undefined {
  const ponta = alvo.closest('[data-muro-ponta]');
  const doMuro = ponta?.getAttribute('data-muro-ponta');
  const qual = ponta?.getAttribute('data-ponta');
  if (doMuro !== null && doMuro !== undefined && (qual === 'de' || qual === 'ate')) {
    return { tipo: 'PONTA', id: doMuro as StructuralPieceId, ponta: qual };
  }
  const pilar = alvo.closest('[data-pilar-contencao]')?.getAttribute('data-pilar-contencao');
  if (pilar !== null && pilar !== undefined) {
    return { tipo: 'PILAR_CONTENCAO', id: pilar as StructuralPieceId };
  }
  return undefined;
}

export function useContencaoNaPrancha({
  draft,
  escala,
  zoom,
  ferramenta,
  parado,
  dispatch,
  selecionar,
}: {
  draft: TypologyDraft;
  escala: number | undefined;
  zoom: number;
  /** A ferramenta ligada, se for uma destas. Parada, a prancha só seleciona e arrasta. */
  ferramenta: FerramentaDaContencao | undefined;
  /** A prancha está parada — sem ferramenta nenhuma ligada? */
  parado: boolean;
  dispatch: React.Dispatch<WorkspaceAction>;
  selecionar: (peca: PecaDaContencao) => void;
}) {
  const muros = useMemo(
    () => (draft.retainingWalls ?? []).filter((muro) => muro.deletedAt === undefined),
    [draft.retainingWalls],
  );
  const pilares = useMemo(
    () => (draft.retainingColumns ?? []).filter((pilar) => pilar.deletedAt === undefined),
    [draft.retainingColumns],
  );

  /** Os dois pontos do eixo do muro, em pixel da prancha. */
  const [eixo, setEixo] = useState<readonly Pixel[]>([]);
  const [nomeDigitadoDoMuro, setNomeDigitadoDoMuro] = useState<string | undefined>(undefined);
  /** Metros. */
  const [alturaDoMuro, setAlturaDoMuro] = useState(3);
  /** Centímetros, como a obra fala a espessura. */
  const [espessuraEmCm, setEspessuraEmCm] = useState(30);

  /** O centro do pilar clicado, já encaixado — ausente até o clique. Metros. */
  const [centroDoPilar, setCentroDoPilar] = useState<Point | undefined>(undefined);
  const [nomeDigitadoDoPilar, setNomeDigitadoDoPilar] = useState<string | undefined>(undefined);
  /** Centímetros: o primeiro número de "30 × 50" é o ao longo do muro. */
  const [larguraEmCm, setLarguraEmCm] = useState(30);
  const [profundidadeEmCm, setProfundidadeEmCm] = useState(50);
  /** Metros — só o que foi digitado; sem isso, vale a altura do muro encaixado. */
  const [alturaDigitadaDoPilar, setAlturaDigitadaDoPilar] = useState<number | undefined>(undefined);

  const [cursor, setCursor] = useState<Pixel | undefined>(undefined);
  const [arrastando, setArrastando] = useState<ArrastoDaContencao | undefined>(undefined);
  /** Quando o último arrasto terminou — o clique que vem junto com ele não é de ninguém. */
  const cliqueDoArrasto = useRef<number | undefined>(undefined);

  // Trocar de tipologia ou de ferramenta descarta o que estava na mão.
  const emDesenho = draft.unitTypeId;
  useEffect(() => {
    setEixo([]);
    setCentroDoPilar(undefined);
    setCursor(undefined);
    setArrastando(undefined);
  }, [emDesenho, ferramenta]);

  const nomeDoMuro = nomeDigitadoDoMuro ?? proximoNomeDeMuro(muros);
  const nomeDoPilar = nomeDigitadoDoPilar ?? proximoNomeDePilarDeContencao(pilares);

  /** O eixo do muro de agora, em metros — ausente enquanto faltar um dos pontos. */
  const eixoEmMetros = useMemo((): { readonly from: Point; readonly to: Point } | undefined => {
    const [a, b] = eixo;
    if (escala === undefined || a === undefined || b === undefined) return undefined;
    return { from: pixelToMetres(a, escala), to: pixelToMetres(b, escala) };
  }, [escala, eixo]);

  const impedimentoDoMuro = useMemo((): string | undefined => {
    const conferido = checkRetainingWallFields(
      {
        name: nomeDoMuro,
        from: eixoEmMetros?.from ?? { x: 0, y: 0 },
        to: eixoEmMetros?.to ?? { x: 1, y: 0 },
        height: alturaDoMuro,
        thickness: espessuraEmCm / 100,
      },
      muros,
    );
    return conferido.ok ? undefined : conferido.error.message;
  }, [nomeDoMuro, eixoEmMetros, alturaDoMuro, espessuraEmCm, muros]);

  /** O pilar de agora, encaixado ou solto — o que o painel e o desenho mostram. */
  const encaixeDoPilar = useMemo(
    () => (centroDoPilar === undefined ? undefined : encaixarNoMuro(centroDoPilar, muros)),
    [centroDoPilar, muros],
  );
  const alturaDoPilar =
    alturaDigitadaDoPilar ?? encaixeDoPilar?.muro?.height ?? ALTURA_PADRAO_DO_PILAR;

  const impedimentoDoPilar = useMemo((): string | undefined => {
    const conferido = checkRetainingColumnFields(
      {
        name: nomeDoPilar,
        center: centroDoPilar ?? { x: 0, y: 0 },
        sectionWidth: larguraEmCm / 100,
        sectionDepth: profundidadeEmCm / 100,
        height: alturaDoPilar,
        angle: encaixeDoPilar?.angulo ?? 0,
      },
      pilares,
    );
    return conferido.ok ? undefined : conferido.error.message;
  }, [
    nomeDoPilar,
    centroDoPilar,
    larguraEmCm,
    profundidadeEmCm,
    alturaDoPilar,
    encaixeDoPilar,
    pilares,
  ]);

  /** Há traço pela metade? Aí o clique é do traço, e a peça que está embaixo deixa passar. */
  const emTraco = eixo.length > 0;

  /**
   * O muro e o pilar respondem a clique só com a prancha parada: o clique do muro novo e o do
   * pilar caem justamente sobre os que já estão desenhados.
   */
  const clicaveis = parado;

  /** Onde o ponto do eixo cai: no esquadro com o ponto anterior, ou onde foi clicado. */
  function pontoDoEixo(bruto: Pixel, anterior?: Pixel): Pixel {
    return anterior === undefined
      ? bruto
      : resolvePoint(bruto, {
          previous: anterior,
          vertexTolerance: 0,
          orthogonalTolerance: FOLGA_ORTOGONAL / zoom,
        });
  }

  /** O clique no vazio da prancha, com uma das ferramentas ligadas. */
  function clicarNaPrancha(bruto: Pixel): void {
    if (escala === undefined) return;

    if (ferramenta === 'MURO') {
      const primeiro = eixo[0];
      if (eixo.length === 1 && primeiro !== undefined) {
        const ponto = pontoDoEixo(bruto, primeiro);
        // O segundo clique no mesmo lugar não é eixo de comprimento zero.
        if (distance(primeiro, ponto) < FOLGA_PONTO / zoom) return;
        setEixo([primeiro, ponto]);
        return;
      }
      setEixo([pontoDoEixo(bruto)]);
      return;
    }

    if (ferramenta === 'PILAR_CONTENCAO') {
      setCentroDoPilar(encaixarNoMuro(pixelToMetres(bruto, escala), muros).centro);
    }
  }

  /** O botão desceu no pilar ou na ponta de um muro: se andar, a peça vai junto. */
  function apertarNaPeca(alvo: Element, ponto: Pixel): boolean {
    if (escala === undefined || !clicaveis) return false;
    const lido = lerAlvo(alvo);
    if (lido === undefined) return false;
    setArrastando({ ...lido, origem: ponto, atual: ponto, moveu: false });
    return true;
  }

  /** O mouse andou: a peça na mão e o segmento vivo. */
  function acompanhar(ponto: Pixel): boolean {
    if (arrastando !== undefined) {
      setArrastando({
        ...arrastando,
        atual: ponto,
        moveu: arrastando.moveu || distance(arrastando.origem, ponto) > FOLGA_DO_ARRASTO / zoom,
      });
      return true;
    }
    // O segmento vivo só existe com traço pela metade; fora disso o cursor não custa render.
    if (ferramenta === 'MURO' && eixo.length === 1) setCursor(ponto);
    return false;
  }

  /** O botão subiu: grava o movimento da peça. */
  function soltar(ponto: Pixel): boolean {
    const corrente = arrastando;
    if (corrente === undefined) return false;
    setArrastando(undefined);
    if (escala === undefined) return true;
    // Soltar onde apertou é clique, e clicar seleciona: a peça não sai do lugar.
    if (!corrente.moveu && distance(corrente.origem, ponto) <= FOLGA_DO_ARRASTO / zoom) {
      return true;
    }
    cliqueDoArrasto.current = Date.now();

    if (corrente.tipo === 'PILAR_CONTENCAO') {
      const pilar = pilares.find((candidato) => candidato.id === corrente.id);
      if (pilar === undefined) return true;
      const destino = encaixarNoMuro(
        {
          x: pilar.center.x + (ponto.x - corrente.origem.x) * escala,
          y: pilar.center.y + (ponto.y - corrente.origem.y) * escala,
        },
        muros,
        pilar.angle,
      );
      dispatch({
        type: 'UPDATE_RETAINING_COLUMN',
        pieceId: corrente.id,
        center: destino.centro,
        angle: destino.angulo,
      });
    } else {
      const muro = muros.find((candidato) => candidato.id === corrente.id);
      if (muro === undefined) return true;
      const outra = metresToPixel(corrente.ponta === 'de' ? muro.to : muro.from, escala);
      const destino = pixelToMetres(pontoDoEixo(ponto, outra), escala);
      dispatch({
        type: 'UPDATE_RETAINING_WALL',
        pieceId: corrente.id,
        ...(corrente.ponta === 'de' ? { from: destino } : { to: destino }),
      });
    }
    return true;
  }

  /**
   * O clique que o fim do arrasto dispara é do arrasto; consome-o, e só uma vez.
   *
   * O navegador o entrega no mesmo instante em que o botão sobe; a janela existe para que um
   * arrasto cujo clique nunca veio — soltar fora da prancha — não engula o clique seguinte,
   * que é de outra pessoa.
   */
  function foiCliqueDoArrasto(): boolean {
    const desde = cliqueDoArrasto.current;
    cliqueDoArrasto.current = undefined;
    return desde !== undefined && Date.now() - desde < JANELA_DO_CLIQUE_MS;
  }

  function adicionarMuro(): void {
    if (eixoEmMetros === undefined || impedimentoDoMuro !== undefined) return;
    dispatch({
      type: 'ADD_RETAINING_WALL',
      name: nomeDoMuro,
      from: eixoEmMetros.from,
      to: eixoEmMetros.to,
      height: alturaDoMuro,
      thickness: espessuraEmCm / 100,
    });
    setEixo([]);
    setCursor(undefined);
    setNomeDigitadoDoMuro(undefined);
  }

  function adicionarPilar(): void {
    if (centroDoPilar === undefined || impedimentoDoPilar !== undefined) return;
    dispatch({
      type: 'ADD_RETAINING_COLUMN',
      name: nomeDoPilar,
      center: centroDoPilar,
      sectionWidth: larguraEmCm / 100,
      sectionDepth: profundidadeEmCm / 100,
      height: alturaDoPilar,
      angle: encaixeDoPilar?.angulo ?? 0,
    });
    setCentroDoPilar(undefined);
    setNomeDigitadoDoPilar(undefined);
    setAlturaDigitadaDoPilar(undefined);
  }

  return {
    ferramenta,
    muros,
    pilares,
    emTraco,
    // O muro
    eixo,
    cursor,
    eixoEmMetros,
    nomeDoMuro,
    digitarNomeDoMuro: setNomeDigitadoDoMuro,
    alturaDoMuro,
    mudarAlturaDoMuro: setAlturaDoMuro,
    espessuraEmCm,
    mudarEspessuraEmCm: setEspessuraEmCm,
    impedimentoDoMuro,
    adicionarMuro,
    refazerMuro: () => {
      setEixo([]);
      setCursor(undefined);
    },
    // O pilar
    centroDoPilar,
    encaixeDoPilar,
    nomeDoPilar,
    digitarNomeDoPilar: setNomeDigitadoDoPilar,
    larguraEmCm,
    mudarLarguraEmCm: setLarguraEmCm,
    profundidadeEmCm,
    mudarProfundidadeEmCm: setProfundidadeEmCm,
    alturaDoPilar,
    mudarAlturaDoPilar: setAlturaDigitadaDoPilar,
    impedimentoDoPilar,
    adicionarPilar,
    refazerPilar: () => setCentroDoPilar(undefined),
    // O que os eventos do desenho chamam
    arrastando,
    clicaveis,
    apertarNaPeca,
    acompanhar,
    soltar,
    cancelarArrasto: () => setArrastando(undefined),
    foiCliqueDoArrasto,
    clicarNaPrancha,
    clicarNoMuro: (id: StructuralPieceId) => {
      if (foiCliqueDoArrasto()) return;
      selecionar({ tipo: 'MURO', id });
    },
    clicarNoPilar: (id: StructuralPieceId) => {
      if (foiCliqueDoArrasto()) return;
      selecionar({ tipo: 'PILAR_CONTENCAO', id });
    },
  };
}

export type ContencaoNaPrancha = ReturnType<typeof useContencaoNaPrancha>;
